// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {
  dynamicMatchWithMapping,
  MatchMode,
} from '@wandb/weave/common/util/columnMatching';
import {cloneDeep as _cloneDeep} from 'lodash';
import React, {useCallback, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Dropdown, Grid, Icon, Input, List} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import type * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import * as TableCols from '../../../util/tablecols';
import TableEditorColumnField from '../../TableEditorColumnField';
import TableEditorColumnListContainer from '../../TableEditorColumnListContainer';

const MAX_COLUMNS_SHOWN = 100;

interface TableEditorProps {
  config: RunFeed.Config;
  allColumns: string[];
  fixedColumns?: string[]; // by accessor

  searchQuery: string;
  searchMode: MatchMode;
  setMatchMode: (mode: MatchMode) => void;

  objectType?: string; // Just a string used in the UI. defaults to Columns, but is Parameters in the parameter importance panel
  setSearch(search: string): void;
  update(config: RunFeed.Config): void;
}

const ColumnSearchInput = styled(Input)`
  width: 100%;
  margin-left: 5px;
  & > input {
    border: none !important;
    border-radius: 0 !important;
    border-bottom: 1px solid #d2d2d2 !important;
  }
`;

const TableEditorBase: React.FC<TableEditorProps> = React.memo(
  ({
    config,
    allColumns,
    fixedColumns,
    searchQuery,
    searchMode,
    setMatchMode,
    objectType: propsObjectType,
    setSearch,
    update,
  }) => {
    const [draggingColumn, setDraggingColumn] = useState<string | undefined>();
    const [visibleColumnListEl, setVisibleColumnListEl] =
      React.useState<HTMLDivElement | null>(null);

    const resetDraggingState = useCallback(() => {
      setDraggingColumn(undefined);
    }, []);

    const updateConfig: typeof update = useCallback(
      conf => {
        conf.columnConfigModified = true;
        update(conf);
      },
      [update]
    );

    const dropField = useCallback(
      (dropColumn: string, visible: boolean) => {
        let conf = _cloneDeep(config);
        if (draggingColumn) {
          if (visible) {
            conf = TableCols.showColumns(conf, [draggingColumn]);
            conf = TableCols.moveBefore(conf, draggingColumn, dropColumn);
          } else {
            conf = TableCols.hideColumns(conf, [draggingColumn]);
          }
        }
        updateConfig(conf);
      },
      [config, draggingColumn, updateConfig]
    );

    const onContainerDrop = useCallback(
      (visible: boolean) => {
        let conf = _cloneDeep(config);
        if (draggingColumn) {
          if (visible) {
            conf = TableCols.showColumns(conf, [draggingColumn]);
            conf = TableCols.moveToEnd(conf, draggingColumn);
            window.setTimeout(() => {
              if (visibleColumnListEl) {
                visibleColumnListEl.scrollTo({
                  top: visibleColumnListEl.scrollHeight,
                });
              }
            });
          } else {
            conf = TableCols.hideColumns(conf, [draggingColumn]);
          }
        }
        updateConfig(conf);
      },
      [config, draggingColumn, updateConfig, visibleColumnListEl]
    );

    const showColumn = useCallback(
      (columnAccessor: string) => {
        updateConfig(TableCols.showColumns(config, [columnAccessor], true));
      },
      [config, updateConfig]
    );

    const hideColumn = useCallback(
      (columnAccessor: string) => {
        updateConfig(TableCols.hideColumns(config, [columnAccessor]));
      },
      [config, updateConfig]
    );

    const showAll = useCallback(
      (hiddenColumns: string[]) => {
        updateConfig(TableCols.showColumns(config, hiddenColumns, true));
      },
      [config, updateConfig]
    );

    const hideAllUnpinned = useCallback(
      (visibleColumns: string[]) => {
        updateConfig(
          TableCols.hideColumns(
            config,
            visibleColumns.filter(c => !config.columnPinned[c])
          )
        );
      },
      [config, updateConfig]
    );

    const togglePinned = useCallback(
      (column: string) => {
        updateConfig(TableCols.togglePinned(config, column));
      },
      [config, updateConfig]
    );

    const objectType = propsObjectType || 'Columns';

    const getSearchMatches = useCallback(
      (columns: string[]) =>
        dynamicMatchWithMapping(searchMode, columns, searchQuery, column =>
          Run.keyStringDisplayName(column)
        ),
      [searchQuery, searchMode]
    );

    const searchedColumnsHidden = getSearchMatches(
      allColumns.filter(cAccessor => !config.columnVisible[cAccessor])
    );

    const searchedColumnsVisible = getSearchMatches(config.columnOrder);

    return (
      <Grid className="table-editor">
        {allColumns.length > TableCols.COLUMN_LIMIT && (
          <Grid.Row>
            <Grid.Column>
              <div style={{fontStyle: 'italic'}}>
                <Icon name="warning sign" style={{marginRight: '5px'}} />
                {`Table will only display up to ${TableCols.COLUMN_LIMIT} columns.`}
              </div>
            </Grid.Column>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Column>
            <div style={{display: 'flex'}}>
              <Dropdown
                compact
                selection
                options={[
                  {
                    text: 'Fuzzy',
                    value: 'fuzzy',
                  },
                  {
                    text: 'Exact',
                    value: 'exact',
                  },
                  {
                    text: 'RegExp',
                    value: 'regex',
                  },
                ]}
                value={searchMode}
                onChange={(_, {value}) => setMatchMode(value as any)}
              />
              <ColumnSearchInput
                size="small"
                icon="search"
                placeholder={`Search ${objectType}`}
                loading={false}
                onChange={(e: any, {value}: any) => setSearch(value)}
              />
            </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
            <TableEditorColumnListContainer
              visibleColumns={false}
              onDrop={() => onContainerDrop(false)}>
              <h5>
                Hidden {objectType}
                <span className="count-tag">
                  {searchedColumnsHidden.length}
                </span>
              </h5>
              <div className="items-scroll-list">
                {searchedColumnsHidden.slice(0, 100).map(column => {
                  return (
                    <TableEditorColumnField
                      key={column}
                      disabled={false}
                      column={column}
                      config={config}
                      onDragStart={(e: React.DragEvent) => {
                        e.dataTransfer.setData('text', ''); // this is necessary for drag+drop to work in firefox
                        setDraggingColumn(column);
                      }}
                      onDragEnd={() => resetDraggingState}
                      onDrop={dropColumn => dropField(dropColumn, false)}
                      onPinClick={null}
                      dragging={column === draggingColumn}
                      searchQuery={searchQuery}
                      searchMode={searchMode}
                      onClick={() => showColumn(column)}
                    />
                  );
                })}
                {searchedColumnsHidden.length >= MAX_COLUMNS_SHOWN && (
                  <List.Item className="hint-text">{`Limited to ${MAX_COLUMNS_SHOWN} results. Use search to find other columns.`}</List.Item>
                )}
              </div>
              <Button
                className="right tiny wb-icon-button"
                onClick={() => showAll(searchedColumnsHidden)}>
                <LegacyWBIcon name="next" />
                Show all
              </Button>
            </TableEditorColumnListContainer>
          </Grid.Column>
          <Grid.Column width={8}>
            <TableEditorColumnListContainer
              visibleColumns={true}
              onDrop={() => onContainerDrop(true)}>
              <h5>
                Visible {objectType}
                <span className="count-tag">
                  {searchedColumnsVisible.length}
                </span>
                {config.columnOrder.length === TableCols.COLUMN_LIMIT && (
                  <span className="column-limit-msg">Column limit reached</span>
                )}
              </h5>
              <div
                className="items-scroll-list"
                ref={node => setVisibleColumnListEl(node)}>
                {searchedColumnsVisible.slice(0, 100).map((column, i) => {
                  return (
                    <TableEditorColumnField
                      key={i}
                      disabled={(fixedColumns || []).indexOf(column) !== -1}
                      column={column}
                      config={config}
                      onDragStart={(e: React.DragEvent) => {
                        e.dataTransfer.setData('text', ''); // this is necessary for drag+drop to work in firefox
                        setDraggingColumn(column);
                      }}
                      onDragEnd={() => resetDraggingState}
                      onDrop={dropColumn => dropField(dropColumn, true)}
                      onPinClick={() => togglePinned(column)}
                      dragging={column === draggingColumn}
                      searchQuery={searchQuery}
                      searchMode={searchMode}
                      onClick={() => hideColumn(column)}
                    />
                  );
                })}
                {searchedColumnsVisible.length >= MAX_COLUMNS_SHOWN && (
                  <List.Item className="hint-text">{`Limited to ${MAX_COLUMNS_SHOWN} results. Use search to find other columns.`}</List.Item>
                )}
              </div>
              <Button
                className="tiny wb-icon-button"
                onClick={() => hideAllUnpinned(searchedColumnsVisible)}>
                <LegacyWBIcon name="previous" />
                Hide all
              </Button>
            </TableEditorColumnListContainer>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
);

export default TableEditorBase;
